import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import { getProject } from '../services/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { TasksTab } from '../components/project/TasksTab';
import { ServiceOrdersTab } from '../components/project/ServiceOrdersTab';
import { ProjectSettings } from '../components/project/ProjectSettings';
import { HoursBankTab } from '../components/project/HoursBankTab';
import { PermissionsTab } from '../components/permissions/PermissionsTab';
import { TicketsTab } from '../components/project/TicketsTab';
import { PermissionsProvider, usePermissions } from '../contexts/PermissionsContext';

interface Project {
  _id: string;
  name: string;
  description?: string;
  hoursTrackingEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

const ProjectDetailContent: React.FC<{ project: Project; settingsOpen: boolean; setSettingsOpen: (open: boolean) => void }> = ({
  project,
  settingsOpen,
  setSettingsOpen
}) => {
  const navigate = useNavigate();
  const { isOwnerOrAdmin } = usePermissions();
  const [activeTab, setActiveTab] = useState<'tasks' | 'serviceOrders' | 'hoursBank' | 'permissions' | 'tickets'>('serviceOrders');

  const tabs = [
    { id: 'serviceOrders' as const, label: 'Ordem de Serviços' },
    { id: 'tasks' as const, label: 'Tarefas' },
    { id: 'tickets' as const, label: 'Chamados' },
    ...(project.hoursTrackingEnabled ? [{ id: 'hoursBank' as const, label: 'Banco de Horas Mensal' }] : []),
    ...(isOwnerOrAdmin() ? [{ id: 'permissions' as const, label: 'Permissões' }] : [])
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header + Tabs (fixed, never scroll) */}
      <div className="flex-shrink-0 px-6 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/projects')}
              className="border-white/20 hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-heading text-foreground-primary">
                {project.name}
              </h1>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="border-white/20 hover:bg-white/10"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurações
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'tasks' | 'serviceOrders' | 'hoursBank' | 'permissions' | 'tickets')}
              className={`px-6 py-4 text-sm font-medium relative ${
                activeTab === tab.id
                  ? 'text-blue-400'
                  : 'text-foreground-secondary hover:text-foreground-primary'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content (fills remaining space) */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === 'tickets' ? (
          <div className="h-full px-6 py-4">
            <TicketsTab projectId={project._id} />
          </div>
        ) : (
          <div className="h-full overflow-y-auto custom-scrollbar px-6 py-6 space-y-6">
            {activeTab === 'tasks' && <TasksTab projectId={project._id} />}
            {activeTab === 'serviceOrders' && <ServiceOrdersTab projectId={project._id} hoursTrackingEnabled={project.hoursTrackingEnabled} />}
            {activeTab === 'hoursBank' && <HoursBankTab projectId={project._id} />}
            {activeTab === 'permissions' && <PermissionsTab />}
          </div>
        )}
      </div>

      {/* Project Settings Modal */}
      <ProjectSettings
        projectId={project._id}
        projectName={project.name}
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
};

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setProject(null);
        setLoading(false);
        return;
      }

      try {
        const response = await getProject(projectId);
        setProject(response.data.data.project);
      } catch (error) {
        console.error('Error loading project:', error);
        setProject(null);
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  if (loading) {
    return (
      <Layout currentPage="projects">
        <div className="h-full flex items-center justify-center">
          <LoadingSpinner message="Carregando projeto" />
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout currentPage="projects">
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading text-foreground-primary mb-2">
              Projeto não encontrado
            </h2>
            <Button onClick={() => navigate('/projects')} variant="outline">
              Voltar para Projetos
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPage="projects">
      <PermissionsProvider projectId={project._id}>
        <ProjectDetailContent
          project={project}
          settingsOpen={settingsOpen}
          setSettingsOpen={setSettingsOpen}
        />
      </PermissionsProvider>
    </Layout>
  );
};

export default ProjectDetail;